import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Box,
    Typography,
    Card,
    Chip,
    TextField,
    InputAdornment,
    Paper,
    Tabs,
    Tab,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Snackbar,
    Alert,
    Avatar,
    Divider,
    Grid,
    DialogContentText,
    Tooltip,
    TooltipProps,
    tooltipClasses,
    Popover,
    CircularProgress,
} from '@mui/material';
import {
    Search,
    Report,
    CheckCircle,
    Pending,
    HourglassEmpty,
    Cancel,
    FilterList,
    Visibility,
    Edit,
    Person,
    Groups,
    Image as ImageIcon,
    Delete,
    Close,
    CalendarToday,
    Business,
    Category,
    LocationCity,
    Send,
    Schedule,
    ArrowBackIos,
    ArrowForwardIos,
    Download,
    PictureAsPdf,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel } from '@mui/x-data-grid';
import { useDebounce } from '../../utils/debounce';
import api from '../../api/axios';
import { useAuth } from '../../contexts/AuthContext';
import CitizenDetailsDialog from '../../components/admin/CitizenDetailsDialog';

interface Complaint {
    id: number;
    ticket_id: string;
    ticket_number: number;
    description: string;
    status: 'pending' | 'in_progress' | 'resolved' | 'rejected';
    priority: string;
    complaint_type: any; // Can be string or object due to collision
    constituency?: { id: number; name: string };
    complaint_department?: { id: number; name: string };
    individual?: { citizen_id: number; citizen?: { name: string; voter_id: string; phone: string; village?: { id: number; name: string; mandal?: { id: number; name: string } }; voting_booth?: { id: number; name: string } }; closure_otp?: string };
    group?: { name: string; description: string };
    images?: { id: number; name: string; path: string; signed_url?: string }[];
    audio?: string;
    created_at: string;
    updated_at: string;
    rejection_reason?: string;
    created_by?: { id: number; name: string };
}

interface ComplaintNote {
    id: number;
    complaint_id: number;
    user_id: number;
    note: string;
    estimated_resolution_date: string | null;
    created_at: string;
    updated_at: string;
    user?: { id: number; name: string };
}

const BootstrapTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    },
}));

const statusConfig = {
    pending: { label: 'Pending', color: 'warning', icon: <Pending /> },
    in_progress: { label: 'In Progress', color: 'info', icon: <HourglassEmpty /> },
    resolved: { label: 'Resolved', color: 'success', icon: <CheckCircle /> },
    rejected: { label: 'Rejected', color: 'error', icon: <Cancel /> },
};

const priorityConfig = {
    low: { label: 'Low', color: 'default' },
    medium: { label: 'Medium', color: 'warning' },
    high: { label: 'High', color: 'error' },
};

const Complaints = () => {
    const queryClient = useQueryClient();
    const { hasPermission } = useAuth();

    // State
    const [pageModel, setPageModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const [selectedTab, setSelectedTab] = useState('all');
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success',
    });

    // View Dialog
    const [viewDialog, setViewDialog] = useState<{ open: boolean; complaint: Complaint | null; loading: boolean }>({
        open: false,
        complaint: null,
        loading: false,
    });

    // Status Update Dialog
    const [statusDialog, setStatusDialog] = useState<{ open: boolean; complaint: Complaint | null }>({
        open: false,
        complaint: null,
    });
    const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; complaintId: number | null }>({
        open: false,
        complaintId: null,
    });
    const [newStatus, setNewStatus] = useState('');
    const [rejectionReason, setRejectionReason] = useState('');
    const [otp, setOtp] = useState('');

    // Citizen Details Dialog
    const [citizenDialog, setCitizenDialog] = useState<{ open: boolean; citizen: any | null }>({ open: false, citizen: null });

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('');
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    // Notes State
    const [notes, setNotes] = useState<ComplaintNote[]>([]);
    const [notesLoading, setNotesLoading] = useState(false);
    const [newNote, setNewNote] = useState('');
    const [newNoteResolutionDate, setNewNoteResolutionDate] = useState('');
    const [addingNote, setAddingNote] = useState(false);

    // Image Gallery State
    const [imageGallery, setImageGallery] = useState<{ open: boolean; currentIndex: number; images: { id: number; name: string; url: string }[] }>({ open: false, currentIndex: 0, images: [] });

    // Export State
    const [exporting, setExporting] = useState(false);

    // Fetch Complaint Types
    const { data: complaintTypesVal } = useQuery({
        queryKey: ['complaint-types'],
        queryFn: async () => {
            const res = await api.get('/tenant/complaint-types');
            return res.data;
        }
    });

    // Fetch Departments
    const { data: departmentsVal } = useQuery({
        queryKey: ['complaint-departments'],
        queryFn: async () => {
            const res = await api.get('/tenant/complaint-departments');
            return res.data;
        }
    });

    const handleQuickFilter = (days: number) => {
        const end = new Date();
        const start = new Date();
        start.setDate(end.getDate() - days);
        setEndDate(end.toISOString().split('T')[0]);
        setStartDate(start.toISOString().split('T')[0]);
    };

    // Fetch Complaints
    const { data: complaintsData, isLoading } = useQuery({
        queryKey: ['complaints', pageModel.page, pageModel.pageSize, debouncedSearch, selectedTab, startDate, endDate, selectedType, selectedDepartment, selectedPriority],
        queryFn: async () => {
            const params: any = {
                page: pageModel.page + 1,
                per_page: pageModel.pageSize,
            };
            if (debouncedSearch) params.search = debouncedSearch;
            if (selectedTab !== 'all') params.status = selectedTab;
            if (startDate) params.start_date = startDate;
            if (endDate) params.end_date = endDate;
            if (selectedType) params.complaint_type_id = selectedType;
            if (selectedDepartment) params.complaint_department_id = selectedDepartment;
            if (selectedPriority) params.priority = selectedPriority;

            const res = await api.get('/tenant/complaints', { params });
            return res.data;
        },
    });




    // Update Status Mutation
    const updateStatusMutation = useMutation({
        mutationFn: async ({ id, status, rejection_reason, otp }: { id: number; status: string; rejection_reason?: string; otp?: string }) => {
            return api.patch(`/tenant/complaints/${id}/status`, { status, rejection_reason, otp });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['complaints'] });
            setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
            handleCloseStatusDialog();
        },
        onError: (error: any) => {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to update status',
                severity: 'error',
            });
        },
    });

    // Send Closure OTP Mutation
    const sendClosureOtpMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.post(`/tenant/complaints/${id}/send-closure-otp`);
        },
        onSuccess: () => {
            setSnackbar({ open: true, message: 'Closure OTP sent to citizen successfully', severity: 'success' });
        },
        onError: (error: any) => {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to send closure OTP',
                severity: 'error',
            });
        },
    });

    // Delete Mutation
    const deleteComplaintMutation = useMutation({
        mutationFn: async (id: number) => {
            return api.delete(`/tenant/complaints/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['complaints'] });
            setSnackbar({ open: true, message: 'Citizen request deleted successfully', severity: 'success' });
            handleCloseDeleteDialog();
        },
        onError: (error: any) => {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to delete citizen request',
                severity: 'error',
            });
        },
    });

    const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
        setSelectedTab(newValue);
        setPageModel({ ...pageModel, page: 0 });
    };

    const handleOpenViewDialog = async (complaint: Complaint) => {
        setViewDialog({ open: true, complaint: null, loading: true });
        setNotes([]);
        setNewNote('');
        setNewNoteResolutionDate('');
        try {
            const res = await api.get(`/tenant/complaints/${complaint.id}`);
            setViewDialog({ open: true, complaint: res.data?.data || res.data, loading: false });
            // Fetch notes
            fetchNotes(complaint.id);
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to load citizen request details',
                severity: 'error',
            });
            setViewDialog({ open: false, complaint: null, loading: false });
        }
    };

    const fetchNotes = async (complaintId: number) => {
        setNotesLoading(true);
        try {
            const res = await api.get(`/tenant/complaints/${complaintId}/notes`);
            setNotes(res.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch notes:', error);
        } finally {
            setNotesLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!viewDialog.complaint || !newNote.trim()) return;
        setAddingNote(true);
        try {
            await api.post(`/tenant/complaints/${viewDialog.complaint.id}/notes`, {
                note: newNote.trim(),
                estimated_resolution_date: newNoteResolutionDate || null,
            });
            setNewNote('');
            setNewNoteResolutionDate('');
            fetchNotes(viewDialog.complaint.id);
            setSnackbar({ open: true, message: 'Note added successfully', severity: 'success' });
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to add note',
                severity: 'error',
            });
        } finally {
            setAddingNote(false);
        }
    };

    const handleCloseViewDialog = () => {
        setViewDialog({ open: false, complaint: null, loading: false });
        setNotes([]);
        setNewNote('');
        setNewNoteResolutionDate('');
    };

    const handleOpenStatusDialog = (complaint: Complaint) => {
        setStatusDialog({ open: true, complaint });
        setNewStatus(complaint.status);
        setRejectionReason('');
        setOtp('');
    };

    const handleCloseStatusDialog = () => {
        setStatusDialog({ open: false, complaint: null });
        setNewStatus('');
        setRejectionReason('');
        setOtp('');
    };

    const handleOpenDeleteDialog = (id: number) => {
        setDeleteDialog({ open: true, complaintId: id });
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog({ open: false, complaintId: null });
    };

    const handleDeleteComplaint = () => {
        if (deleteDialog.complaintId) {
            deleteComplaintMutation.mutate(deleteDialog.complaintId);
        }
    };

    const handleUpdateStatus = () => {
        if (!statusDialog.complaint) return;

        updateStatusMutation.mutate({
            id: statusDialog.complaint.id,
            status: newStatus,
            rejection_reason: newStatus === 'rejected' ? rejectionReason : undefined,
            otp: newStatus === 'resolved' ? (otp || '') : undefined,
        });
    };

    // Helper for professional consistent colors
    const stringToColor = (string: string) => {
        const colors = [
            ['#7c3aed', '#ede9fe'], // Violet
            ['#0f766e', '#ccfbf1'], // Teal
            ['#4338ca', '#e0e7ff'], // Indigo
            ['#1d4ed8', '#dbeafe'], // Blue
            ['#be185d', '#fce7f3'], // Pink
            ['#b45309', '#fef3c7'], // Amber
        ];
        let hash = 0;
        for (let i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const columns: GridColDef[] = [
        {
            field: 'index',
            headerName: '#',
            width: 60,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', opacity: 0.7 }}>
                    {(pageModel.page * pageModel.pageSize) + params.api.getAllRowIds().indexOf(params.row.id) + 1}
                </Typography>
            ),
        },
        {
            field: 'citizen_name',
            headerName: 'Citizen',
            flex: 1,
            minWidth: 180,
            renderCell: (params: GridRenderCellParams) => {
                const citizenName = params.row.individual?.citizen?.name || params.row.group?.name || 'N/A';
                const [darkColor, lightColor] = stringToColor(citizenName);
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            p: 0.5,
                            borderRadius: 2,
                            width: '100%',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                        }}
                        onClick={() => handleOpenViewDialog(params.row)}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: lightColor,
                                color: darkColor,
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                border: `1px solid ${darkColor}20`
                            }}
                        >
                            {citizenName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', letterSpacing: '-0.01em', '&:hover': { color: 'primary.main' } }}>
                            {citizenName}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'voter_id',
            headerName: 'Voter ID',
            width: 140,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const voterId = params.row.individual?.citizen?.voter_id || 'N/A';
                return (
                    <Typography variant="body2" fontFamily="monospace" fontWeight={500} sx={{ color: '#475569' }}>
                        {voterId}
                    </Typography>
                );
            },
        },
        {
            field: 'ticket_id',
            headerName: 'Ticket ID',
            width: 130,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.row.ticket_id || `#${String(params.row.id).padStart(6, '0')}`}
                    size="small"
                    sx={{
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        bgcolor: '#eff6ff',
                        color: '#1d4ed8',
                        borderRadius: '6px',
                    }}
                />
            ),
        },
        {
            field: 'complaint_type',
            headerName: 'Request Type',
            width: 160,
            renderCell: (params: GridRenderCellParams) => {
                const typeName = params.row.complaint_type?.name || 'N/A';
                return (
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a', letterSpacing: '-0.01em' }}>
                        {typeName}
                    </Typography>
                );
            },
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 120,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const isIndividual = params.row.individual;
                return (
                    <Chip
                        icon={isIndividual ? <Person fontSize="small" /> : <Groups fontSize="small" />}
                        label={isIndividual ? 'Individual' : 'Group'}
                        size="small"
                        sx={{
                            borderRadius: '6px',
                            fontWeight: 500,
                            bgcolor: isIndividual ? '#eff6ff' : '#f0fdf4',
                            color: isIndividual ? '#1d4ed8' : '#16a34a',
                            border: 'none'
                        }}
                    />
                );
            },
        },
        {
            field: 'constituency',
            headerName: 'Constituency',
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.row.constituency?.name || 'N/A'}
                    size="small"
                    variant="outlined"
                    sx={{
                        borderRadius: 1.5,
                        fontWeight: 500,
                        borderColor: 'divider',
                        bgcolor: 'rgba(0,0,0,0.01)',
                    }}
                />
            ),
        },
        {
            field: 'complaint_department',
            headerName: 'Department',
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Chip
                    label={params.row.complaint_department?.name || 'N/A'}
                    size="small"
                    sx={{
                        borderRadius: '6px',
                        fontWeight: 500,
                        bgcolor: '#f8fafc',
                        color: '#475569',
                    }}
                />
            ),
        },
        {
            field: 'priority',
            headerName: 'Priority',
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const priorityStyles = {
                    low: { bgcolor: '#f1f5f9', color: '#64748b' },
                    medium: { bgcolor: '#fef3c7', color: '#b45309' },
                    high: { bgcolor: '#fee2e2', color: '#dc2626' },
                };
                const style = priorityStyles[params.value as keyof typeof priorityStyles] || priorityStyles.medium;
                const config = priorityConfig[params.value as keyof typeof priorityConfig] || priorityConfig.medium;
                return (
                    <Chip
                        label={config.label}
                        size="small"
                        sx={{
                            borderRadius: '6px',
                            fontWeight: 600,
                            ...style
                        }}
                    />
                );
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const statusStyles = {
                    pending: { bgcolor: '#fef3c7', color: '#b45309' },
                    in_progress: { bgcolor: '#dbeafe', color: '#1d4ed8' },
                    resolved: { bgcolor: '#dcfce7', color: '#16a34a' },
                    rejected: { bgcolor: '#fee2e2', color: '#dc2626' },
                };
                const style = statusStyles[params.value as keyof typeof statusStyles] || statusStyles.pending;
                const config = statusConfig[params.value as keyof typeof statusConfig] || statusConfig.pending;
                return (
                    <Chip
                        icon={config.icon}
                        label={config.label}
                        size="small"
                        sx={{
                            borderRadius: '6px',
                            fontWeight: 600,
                            ...style,
                            '& .MuiChip-icon': { color: 'inherit' }
                        }}
                    />
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Created',
            width: 110,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                    {new Date(params.value).toLocaleDateString()}
                </Typography>
            ),
        },
        {
            field: 'created_by',
            headerName: 'Created By',
            width: 150,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => {
                const creatorName = params.row.created_by?.name || 'Unknown';
                return (
                    <Typography variant="body2" fontWeight={500} sx={{ color: '#475569' }}>
                        {creatorName}
                    </Typography>
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            sortable: false,
            filterable: false,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
                <Stack direction="row" spacing={0.5}>
                    <BootstrapTooltip title="View Details" placement="top">
                        <IconButton
                            size="small"
                            onClick={() => handleOpenViewDialog(params.row)}
                            sx={{
                                bgcolor: '#eff6ff',
                                color: '#1d4ed8',
                                '&:hover': { bgcolor: '#dbeafe' },
                            }}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </BootstrapTooltip>
                    {hasPermission('complaints.update') && (
                        <BootstrapTooltip title="Update Status" placement="top">
                            <IconButton
                                size="small"
                                onClick={() => handleOpenStatusDialog(params.row)}
                                sx={{
                                    bgcolor: '#dcfce7',
                                    color: '#16a34a',
                                    '&:hover': { bgcolor: '#bbf7d0' },
                                }}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    )}
                    {hasPermission('complaints.delete') && (
                        <BootstrapTooltip title="Delete" placement="top">
                            <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(params.row.id)}
                                sx={{
                                    bgcolor: '#fee2e2',
                                    color: '#dc2626',
                                    '&:hover': { bgcolor: '#fecaca' },
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </BootstrapTooltip>
                    )}
                </Stack>
            ),
        },
    ];

    const complaints = complaintsData?.data?.data || [];
    const totalCount = complaintsData?.data?.total || 0;

    const getArrayData = (response: any) => {
        if (!response) return [];
        if (Array.isArray(response)) return response;
        if (response.data && Array.isArray(response.data)) return response.data;
        if (response.data?.data && Array.isArray(response.data.data)) return response.data.data;
        return [];
    };

    const complaintTypes = getArrayData(complaintTypesVal);
    const departments = getArrayData(departmentsVal);
    const counts = complaintsData?.counts || {
        all: 0,
        pending: 0,
        in_progress: 0,
        resolved: 0,
        rejected: 0
    };

    // Tab counts from API
    const tabCounts = {
        all: counts.all,
        pending: counts.pending,
        in_progress: counts.in_progress,
        resolved: counts.resolved,
        rejected: counts.rejected,
    };

    const viewingComplaint = viewDialog.complaint;

    return (
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                    color: 'white',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                        <Report sx={{ fontSize: 32 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" fontWeight="bold">Citizen Requests</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Manage and track all citizen requests
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Status Cards */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                mb: 4,
            }}>
                {/* Total Citizen Requests Card */}
                <Box sx={{ flex: '1 1 calc(20% - 24px)', minWidth: 200 }}>
                    <Card
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 20px rgba(102, 126, 234, 0.4)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '100px',
                                height: '100px',
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                transform: 'translate(30%, -30%)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                                <Report sx={{ fontSize: 26 }} />
                            </Avatar>
                            <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                {counts.all}
                            </Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: '0.5px' }}>
                            Total Requests
                        </Typography>
                    </Card>
                </Box>

                {/* Pending Citizen Requests Card */}
                <Box sx={{ flex: '1 1 calc(20% - 24px)', minWidth: 200 }}>
                    <Card
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 20px rgba(246, 211, 101, 0.4)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '100px',
                                height: '100px',
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                transform: 'translate(30%, -30%)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                                <Pending sx={{ fontSize: 26 }} />
                            </Avatar>
                            <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                {counts.pending}
                            </Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: '0.5px' }}>
                            Pending Requests
                        </Typography>
                    </Card>
                </Box>

                {/* In Progress Citizen Requests Card */}
                <Box sx={{ flex: '1 1 calc(20% - 24px)', minWidth: 200 }}>
                    <Card
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 20px rgba(79, 172, 254, 0.4)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '100px',
                                height: '100px',
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                transform: 'translate(30%, -30%)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                                <HourglassEmpty sx={{ fontSize: 26 }} />
                            </Avatar>
                            <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                {counts.in_progress}
                            </Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: '0.5px' }}>
                            In Progress
                        </Typography>
                    </Card>
                </Box>

                {/* Resolved Citizen Requests Card */}
                <Box sx={{ flex: '1 1 calc(20% - 24px)', minWidth: 200 }}>
                    <Card
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 20px rgba(17, 153, 142, 0.4)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '100px',
                                height: '100px',
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                transform: 'translate(30%, -30%)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                                <CheckCircle sx={{ fontSize: 26 }} />
                            </Avatar>
                            <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                {counts.resolved}
                            </Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: '0.5px' }}>
                            Resolved Requests
                        </Typography>
                    </Card>
                </Box>

                {/* Rejected Citizen Requests Card */}
                <Box sx={{ flex: '1 1 calc(20% - 24px)', minWidth: 200 }}>
                    <Card
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: 3,
                            background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)',
                            color: 'white',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 20px rgba(255, 65, 108, 0.4)',
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: '100px',
                                height: '100px',
                                background: 'rgba(255,255,255,0.1)',
                                borderRadius: '50%',
                                transform: 'translate(30%, -30%)',
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                                <Cancel sx={{ fontSize: 26 }} />
                            </Avatar>
                            <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                {counts.rejected}
                            </Typography>
                        </Box>
                        <Typography variant="subtitle2" sx={{ opacity: 0.9, fontWeight: 500, letterSpacing: '0.5px' }}>
                            Rejected Requests
                        </Typography>
                    </Card>
                </Box>
            </Box>

            {/* Search & Filter Bar */}
            <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                    <TextField
                        size="small"
                        placeholder="Search citizen requests..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: { xs: '100%', md: 400 } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<FilterList />}
                            onClick={(e) => setAnchorEl(e.currentTarget)}
                            color={(startDate || endDate || selectedType || selectedDepartment || selectedPriority) ? "primary" : "inherit"}
                        >
                            Filters
                            {(startDate || endDate || selectedType || selectedDepartment || selectedPriority) && (
                                <Box
                                    component="span"
                                    sx={{
                                        ml: 1,
                                        bgcolor: 'primary.main',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    {[startDate, endDate, selectedType, selectedDepartment, selectedPriority].filter(Boolean).length}
                                </Box>
                            )}
                        </Button>
                        {hasPermission('reports.export') && (
                            <Button
                                variant="contained"
                                startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <Download />}
                                onClick={async () => {
                                    setExporting(true);
                                    try {
                                        const params = new URLSearchParams();
                                        if (search) params.append('search', search);
                                        if (selectedTab !== 'all') params.append('status', selectedTab);
                                        if (startDate) params.append('start_date', startDate);
                                        if (endDate) params.append('end_date', endDate);
                                        if (selectedType) params.append('complaint_type_id', selectedType);
                                        if (selectedDepartment) params.append('complaint_department_id', selectedDepartment);

                                        const response = await api.get(`/tenant/complaints/export?${params.toString()}`, {
                                            responseType: 'blob',
                                        });

                                        const url = window.URL.createObjectURL(new Blob([response.data]));
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.setAttribute('download', `complaints_export_${new Date().toISOString().slice(0, 10)}.csv`);
                                        document.body.appendChild(link);
                                        link.click();
                                        link.remove();
                                        window.URL.revokeObjectURL(url);

                                        setSnackbar({ open: true, message: 'Export downloaded successfully!', severity: 'success' });
                                    } catch (error) {
                                        console.error('Export error:', error);
                                        setSnackbar({ open: true, message: 'Failed to export citizen requests', severity: 'error' });
                                    } finally {
                                        setExporting(false);
                                    }
                                }}
                                disabled={exporting}
                                sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}
                            >
                                {exporting ? 'Exporting...' : 'Export'}
                            </Button>
                        )}
                    </Box>
                </Stack>
            </Paper>

            {/* Filter Popover */}
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box sx={{ p: 3, width: 320 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" fontWeight="bold">Filters</Typography>
                        {(startDate || endDate || selectedType || selectedDepartment || selectedPriority) && (
                            <Button
                                size="small"
                                color="error"
                                onClick={() => {
                                    setStartDate('');
                                    setEndDate('');
                                    setSelectedType('');
                                    setSelectedDepartment('');
                                    setSelectedPriority('');
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>

                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Date Range</Typography>
                            <Stack spacing={2}>
                                <TextField
                                    type="date"
                                    label="From"
                                    size="small"
                                    fullWidth
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    type="date"
                                    label="To"
                                    size="small"
                                    fullWidth
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Stack>
                            <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                <Chip label="7 Days" size="small" onClick={() => handleQuickFilter(7)} clickable />
                                <Chip label="15 Days" size="small" onClick={() => handleQuickFilter(15)} clickable />
                                <Chip label="30 Days" size="small" onClick={() => handleQuickFilter(30)} clickable />
                            </Box>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Request Type</Typography>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedType}
                                    displayEmpty
                                    onChange={(e) => setSelectedType(e.target.value)}
                                >
                                    <MenuItem value=""><em>All Types</em></MenuItem>
                                    {complaintTypes.map((type: any) => (
                                        <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Department</Typography>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedDepartment}
                                    displayEmpty
                                    onChange={(e) => setSelectedDepartment(e.target.value)}
                                >
                                    <MenuItem value=""><em>All Departments</em></MenuItem>
                                    {departments.map((dept: any) => (
                                        <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Priority</Typography>
                            <FormControl size="small" fullWidth>
                                <Select
                                    value={selectedPriority}
                                    displayEmpty
                                    onChange={(e) => setSelectedPriority(e.target.value)}
                                >
                                    <MenuItem value=""><em>All Priorities</em></MenuItem>
                                    <MenuItem value="low">Low</MenuItem>
                                    <MenuItem value="medium">Medium</MenuItem>
                                    <MenuItem value="high">High</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>
                </Box>
            </Popover>

            {/* Tabs */}
            <Paper sx={{ mb: 3, borderRadius: 2 }}>
                <Tabs
                    value={selectedTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label={`All (${tabCounts.all})`} value="all" />
                    <Tab
                        icon={<Pending sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={`Pending (${tabCounts.pending})`}
                        value="pending"
                        sx={{ color: 'warning.main' }}
                    />
                    <Tab
                        icon={<HourglassEmpty sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={`In Progress (${tabCounts.in_progress})`}
                        value="in_progress"
                        sx={{ color: 'info.main' }}
                    />
                    <Tab
                        icon={<CheckCircle sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={`Resolved (${tabCounts.resolved})`}
                        value="resolved"
                        sx={{ color: 'success.main' }}
                    />
                    <Tab
                        icon={<Cancel sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={`Rejected (${tabCounts.rejected})`}
                        value="rejected"
                        sx={{ color: 'error.main' }}
                    />
                </Tabs>
            </Paper>

            {/* Data Grid */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid', borderColor: 'divider' }}>
                <DataGrid
                    rows={complaints}
                    columns={columns}
                    loading={isLoading}
                    paginationModel={pageModel}
                    onPaginationModelChange={setPageModel}
                    pageSizeOptions={[10, 25, 50]}
                    rowCount={totalCount}
                    paginationMode="server"
                    disableRowSelectionOnClick
                    autoHeight
                    rowHeight={70}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            color: '#475569',
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        },
                        '& .MuiDataGrid-cell': {
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: '#f8fafc',
                        },
                    }}
                />
            </Card>

            {/* View Details Dialog - Styled like CitizenDetailsDialog */}
            <Dialog open={viewDialog.open} onClose={handleCloseViewDialog} maxWidth="lg" fullWidth>
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 48, height: 48, bgcolor: 'rgba(255,255,255,0.2)', fontWeight: 'bold' }}>
                            <Report />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" fontWeight="bold">
                                {viewDialog.loading ? 'Loading...' : `Ticket ${viewingComplaint?.ticket_id || '#' + String(viewingComplaint?.id).padStart(6, '0')}`}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8, letterSpacing: 1 }}>
                                {viewDialog.loading ? '' : `Created by: ${viewingComplaint?.created_by?.name || 'Unknown'}`}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!viewDialog.loading && viewingComplaint && (
                            <Button
                                onClick={async () => {
                                    try {
                                        const res = await api.get(`/tenant/complaints/${viewingComplaint.id}/pdf`, { responseType: 'blob' });
                                        const blob = new Blob([res.data], { type: 'application/pdf' });
                                        const url = URL.createObjectURL(blob);
                                        window.open(url, '_blank');
                                    } catch {
                                        alert('Failed to generate PDF');
                                    }
                                }}
                                startIcon={<PictureAsPdf />}
                                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                variant="outlined"
                                size="small"
                            >
                                PDF
                            </Button>
                        )}
                        {!viewDialog.loading && hasPermission('complaints.update') && viewingComplaint && (
                            <Button
                                onClick={() => {
                                    handleCloseViewDialog();
                                    handleOpenStatusDialog(viewingComplaint);
                                }}
                                startIcon={<Edit />}
                                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.3)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
                                variant="outlined"
                                size="small"
                            >
                                Update Status
                            </Button>
                        )}
                        <IconButton onClick={handleCloseViewDialog} sx={{ color: 'white' }}>
                            <Close />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ p: 0, bgcolor: '#f8fafc', overflow: 'hidden' }}>
                    {viewDialog.loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                            <CircularProgress />
                        </Box>
                    ) : viewingComplaint && (
                        <Box sx={{ display: 'flex', height: 'calc(100vh - 200px)', maxHeight: 600 }}>
                            {/* Left Side - All Details */}
                            <Box sx={{ flex: 7, overflowY: 'auto', p: 2.5, bgcolor: '#f8fafc' }}>
                                <Stack spacing={2.5}>
                                    {/* Status & Priority Cards */}
                                    <Box sx={{ display: 'flex', gap: 2 }}>
                                        <Paper sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 3,
                                            background: viewingComplaint.status === 'resolved' ? 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)' :
                                                viewingComplaint.status === 'rejected' ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' :
                                                    viewingComplaint.status === 'in_progress' ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)' :
                                                        'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{
                                                    bgcolor: viewingComplaint.status === 'resolved' ? '#16a34a' :
                                                        viewingComplaint.status === 'rejected' ? '#dc2626' :
                                                            viewingComplaint.status === 'in_progress' ? '#2563eb' : '#d97706',
                                                    width: 40,
                                                    height: 40
                                                }}>
                                                    {statusConfig[viewingComplaint.status]?.icon}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500 }}>Status</Typography>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {statusConfig[viewingComplaint.status]?.label}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                        <Paper sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 3,
                                            background: viewingComplaint.priority === 'high' ? 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)' :
                                                viewingComplaint.priority === 'low' ? 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)' :
                                                    'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{
                                                    bgcolor: viewingComplaint.priority === 'high' ? '#dc2626' :
                                                        viewingComplaint.priority === 'low' ? '#64748b' : '#d97706',
                                                    width: 40,
                                                    height: 40
                                                }}>
                                                    🔥
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500 }}>Priority</Typography>
                                                    <Typography variant="body1" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                                                        {viewingComplaint.priority}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                        <Paper sx={{
                                            flex: 1,
                                            p: 2,
                                            borderRadius: 3,
                                            background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                                            border: 'none',
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Avatar sx={{ bgcolor: '#4f46e5', width: 40, height: 40 }}>
                                                    {viewingComplaint.individual ? <Person /> : <Groups />}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500 }}>Type</Typography>
                                                    <Typography variant="body1" fontWeight="bold">
                                                        {viewingComplaint.individual ? 'Individual' : 'Group'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>

                                    {/* Description Card */}
                                    <Paper sx={{
                                        p: 2.5,
                                        borderRadius: 3,
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.05)',
                                    }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{
                                            mb: 1.5,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: '#374151',
                                        }}>
                                            📝 Description
                                        </Typography>
                                        <Typography variant="body2" sx={{
                                            bgcolor: '#f8fafc',
                                            p: 2,
                                            borderRadius: 2,
                                            lineHeight: 1.7,
                                            color: '#4b5563',
                                        }}>
                                            {viewingComplaint.description}
                                        </Typography>
                                    </Paper>

                                    {/* Timeline Info */}
                                    <Paper sx={{
                                        p: 2.5,
                                        borderRadius: 3,
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.05)',
                                    }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: '#374151',
                                        }}>
                                            ⏱️ Timeline
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                bgcolor: '#f0fdf4',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                            }}>
                                                <CalendarToday sx={{ fontSize: 18, color: '#16a34a' }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>Created</Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#16a34a">
                                                        {new Date(viewingComplaint.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                bgcolor: '#eff6ff',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                            }}>
                                                <CalendarToday sx={{ fontSize: 18, color: '#2563eb' }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>Updated</Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#2563eb">
                                                        {new Date(viewingComplaint.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                bgcolor: '#faf5ff',
                                                px: 2,
                                                py: 1,
                                                borderRadius: 2,
                                            }}>
                                                <Person sx={{ fontSize: 18, color: '#7c3aed' }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight={500}>Created By</Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#7c3aed">
                                                        {viewingComplaint.created_by?.name || 'Unknown'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Paper>

                                    {/* Category Info */}
                                    <Paper sx={{
                                        p: 2.5,
                                        borderRadius: 3,
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.05)',
                                    }}>
                                        <Typography variant="subtitle2" fontWeight="bold" sx={{
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            color: '#374151',
                                        }}>
                                            📍 Category & Location
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 6, md: 4 }}>
                                                <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                                                        Category
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#374151">
                                                        {viewingComplaint.complaint_type?.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, md: 4 }}>
                                                <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                                                        Department
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#374151">
                                                        {viewingComplaint.complaint_department?.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, md: 4 }}>
                                                <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                                                        Constituency
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#374151">
                                                        {viewingComplaint.constituency?.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, md: 4 }}>
                                                <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                                                        Mandal
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#374151">
                                                        {viewingComplaint.individual?.citizen?.village?.mandal?.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, md: 4 }}>
                                                <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                                                        Village
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#374151">
                                                        {viewingComplaint.individual?.citizen?.village?.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid size={{ xs: 6, md: 4 }}>
                                                <Box sx={{ bgcolor: '#f8fafc', p: 1.5, borderRadius: 2 }}>
                                                    <Typography variant="caption" color="text.secondary" display="block" fontWeight={500}>
                                                        Voting Booth
                                                    </Typography>
                                                    <Typography variant="body2" fontWeight="bold" color="#374151">
                                                        {viewingComplaint.individual?.citizen?.voting_booth?.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Paper>

                                    {/* Citizen Info */}
                                    {viewingComplaint.individual && (
                                        <Paper sx={{
                                            p: 2.5,
                                            borderRadius: 3,
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)',
                                            border: '1px solid',
                                            borderColor: 'rgba(37, 99, 235, 0.1)',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                                <Typography variant="subtitle2" fontWeight="bold" sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    color: '#1e40af',
                                                }}>
                                                    👤 Citizen Information
                                                </Typography>
                                                {viewingComplaint.individual?.citizen && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<Visibility />}
                                                        onClick={() => setCitizenDialog({ open: true, citizen: viewingComplaint.individual?.citizen })}
                                                        sx={{ textTransform: 'none', borderRadius: 2, fontSize: '0.75rem' }}
                                                    >
                                                        View Citizen
                                                    </Button>
                                                )}
                                            </Box>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 6, md: 3 }}>
                                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>Name</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{viewingComplaint.individual.citizen?.name || 'N/A'}</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 6, md: 3 }}>
                                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>Voter ID</Typography>
                                                        <Typography variant="body2" fontWeight="bold" fontFamily="monospace">{viewingComplaint.individual.citizen?.voter_id || 'N/A'}</Typography>
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 6, md: 3 }}>
                                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>Phone</Typography>
                                                        <Typography variant="body2" fontWeight="bold" fontFamily="monospace">{viewingComplaint.individual.citizen?.phone || 'N/A'}</Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    )}

                                    {/* Group Info */}
                                    {viewingComplaint.group && (
                                        <Paper sx={{
                                            p: 2.5,
                                            borderRadius: 3,
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)',
                                            border: '1px solid',
                                            borderColor: 'rgba(22, 163, 74, 0.1)',
                                        }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#166534',
                                            }}>
                                                👥 Group Information
                                            </Typography>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 12, md: 6 }}>
                                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" fontWeight={500}>Group Name</Typography>
                                                        <Typography variant="body2" fontWeight="bold">{viewingComplaint.group.name}</Typography>
                                                    </Box>
                                                </Grid>
                                                {viewingComplaint.group.description && (
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.7)', p: 1.5, borderRadius: 2 }}>
                                                            <Typography variant="caption" color="text.secondary" fontWeight={500}>Description</Typography>
                                                            <Typography variant="body2">{viewingComplaint.group.description}</Typography>
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Paper>
                                    )}

                                    {/* Rejection Reason */}
                                    {viewingComplaint.status === 'rejected' && viewingComplaint.rejection_reason && (
                                        <Paper sx={{
                                            p: 2.5,
                                            borderRadius: 3,
                                            boxShadow: '0 4px 15px rgba(220, 38, 38, 0.1)',
                                            background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                                            border: '1px solid #fecaca',
                                        }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{
                                                mb: 1.5,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#dc2626',
                                            }}>
                                                ⚠️ Rejection Reason
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#991b1b' }}>{viewingComplaint.rejection_reason}</Typography>
                                        </Paper>
                                    )}

                                    {/* Images */}
                                    {viewingComplaint.images && viewingComplaint.images.length > 0 && (
                                        <Paper sx={{
                                            p: 2.5,
                                            borderRadius: 3,
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.05)',
                                            border: '1px solid',
                                            borderColor: 'rgba(0,0,0,0.05)',
                                        }}>
                                            <Typography variant="subtitle2" fontWeight="bold" sx={{
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                color: '#374151',
                                            }}>
                                                🖼️ Attached Images
                                                <Chip label={viewingComplaint.images.length} size="small" sx={{ bgcolor: '#e0e7ff', color: '#4338ca', fontWeight: 'bold', height: 20 }} />
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                {viewingComplaint.images.map((img, index) => {
                                                    // Use signed URL from API response for secure access
                                                    const imageUrl = img.signed_url || '';
                                                    return (
                                                        <Paper
                                                            key={img.id}
                                                            sx={{
                                                                width: 90,
                                                                height: 90,
                                                                overflow: 'hidden',
                                                                borderRadius: 2.5,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.3s ease',
                                                                border: '2px solid transparent',
                                                                '&:hover': {
                                                                    transform: 'scale(1.08) rotate(1deg)',
                                                                    boxShadow: '0 8px 25px rgba(99, 102, 241, 0.3)',
                                                                    borderColor: '#6366f1',
                                                                },
                                                            }}
                                                            onClick={() => {
                                                                const allImages = viewingComplaint.images!.map((i) => {
                                                                    return { id: i.id, name: i.name, url: i.signed_url || '' };
                                                                });
                                                                setImageGallery({ open: true, currentIndex: index, images: allImages });
                                                            }}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src={imageUrl}
                                                                alt={img.name}
                                                                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            />
                                                        </Paper>
                                                    );
                                                })}
                                            </Box>
                                        </Paper>
                                    )}
                                </Stack>
                            </Box>

                            {/* Right Side - Notes */}
                            <Box sx={{ flex: 5, borderLeft: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
                                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8fafc' }}>
                                    <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                                        Notes & Updates
                                    </Typography>
                                </Box>

                                {/* Notes List - Chat-like UI */}
                                <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
                                    {notesLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : notes.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                            <Typography variant="body2">No notes yet</Typography>
                                        </Box>
                                    ) : (
                                        <Stack spacing={1.5}>
                                            {notes.map((note) => (
                                                <Paper
                                                    key={note.id}
                                                    sx={{
                                                        p: 1.5,
                                                        bgcolor: '#f1f5f9',
                                                        borderRadius: 2,
                                                        borderLeft: '3px solid',
                                                        borderColor: 'primary.main',
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                                                        {note.note}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                                        <Chip
                                                            size="small"
                                                            icon={<Person sx={{ fontSize: 14 }} />}
                                                            label={note.user?.name || 'Unknown'}
                                                            sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#e0e7ff', color: '#4338ca' }}
                                                        />
                                                        {note.estimated_resolution_date && (
                                                            <Chip
                                                                size="small"
                                                                icon={<Schedule sx={{ fontSize: 14 }} />}
                                                                label={`Est: ${new Date(note.estimated_resolution_date).toLocaleDateString()}`}
                                                                sx={{ height: 22, fontSize: '0.7rem', bgcolor: '#fef3c7', color: '#b45309' }}
                                                            />
                                                        )}
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                                                            {new Date(note.created_at).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </Paper>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>

                                {/* Add Note Form */}
                                {hasPermission('complaints.update') && (
                                    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2.5, py: 2, bgcolor: '#f8fafc' }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Add a note..."
                                            value={newNote}
                                            onChange={(e) => setNewNote(e.target.value)}
                                            multiline
                                            rows={2}
                                            sx={{ mb: 1 }}
                                        />
                                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                            <TextField
                                                size="small"
                                                type="date"
                                                label="Est. Resolution (Optional)"
                                                value={newNoteResolutionDate}
                                                onChange={(e) => setNewNoteResolutionDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                sx={{ flex: 1 }}
                                            />
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={handleAddNote}
                                                disabled={addingNote || !newNote.trim()}
                                                endIcon={addingNote ? <CircularProgress size={16} color="inherit" /> : <Send />}
                                                sx={{ minWidth: 100 }}
                                            >
                                                Add Note
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>

            {/* Status Update Dialog */}
            <Dialog open={statusDialog.open} onClose={handleCloseStatusDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Update Status</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Citizen Request</Typography>
                            <Typography variant="body1">{statusDialog.complaint?.complaint_type?.name || `Request #${statusDialog.complaint?.id}`}</Typography>
                        </Box>
                        <Divider />
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={newStatus}
                                label="Status"
                                onChange={(e) => setNewStatus(e.target.value)}
                            >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="in_progress">In Progress</MenuItem>
                                <MenuItem value="resolved">Resolved</MenuItem>
                                <MenuItem value="rejected">Rejected</MenuItem>
                            </Select>
                        </FormControl>

                        {newStatus === 'rejected' && (
                            <TextField
                                fullWidth
                                label="Rejection Reason"
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                multiline
                                rows={3}
                                required
                            />
                        )}

                        {newStatus === 'resolved' && (
                            <Box>
                                {statusDialog.complaint?.complaint_type === 'individual' || statusDialog.complaint?.individual ? (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <TextField
                                            fullWidth
                                            label="Closure OTP"
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value)}
                                            required
                                            helperText="Enter the closure OTP sent to the citizen"
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={() => statusDialog.complaint && sendClosureOtpMutation.mutate(statusDialog.complaint.id)}
                                            disabled={sendClosureOtpMutation.isPending}
                                            sx={{ minWidth: 120, mt: '8px' }}
                                            startIcon={<Send />}
                                        >
                                            {sendClosureOtpMutation.isPending ? 'Sending...' : 'Send OTP'}
                                        </Button>
                                    </Box>
                                ) : (
                                    <TextField
                                        fullWidth
                                        label="OTP for Verification"
                                        value={otp}
                                        onChange={(e) => setOtp(e.target.value)}
                                        required
                                        helperText="Enter the closure OTP"
                                    />
                                )}
                            </Box>
                        )}

                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseStatusDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdateStatus}
                        disabled={updateStatusMutation.isPending || (newStatus === 'rejected' && !rejectionReason) || (newStatus === 'resolved' && !otp)}
                    >
                        {updateStatusMutation.isPending ? 'Updating...' : 'Update'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
                <DialogTitle>Delete Citizen Request</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this citizen request? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDeleteComplaint} color="error" variant="contained" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Image Gallery Dialog */}
            <Dialog
                open={imageGallery.open}
                onClose={() => setImageGallery({ ...imageGallery, open: false })}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(0,0,0,0.95)',
                        boxShadow: 'none',
                        maxHeight: '95vh',
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', py: 1 }}>
                    <Typography variant="body2" color="grey.400">
                        {imageGallery.currentIndex + 1} / {imageGallery.images.length}
                    </Typography>
                    <IconButton onClick={() => setImageGallery({ ...imageGallery, open: false })} sx={{ color: '#fff' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', p: 0, minHeight: 400 }}>
                    {/* Previous Arrow */}
                    {imageGallery.images.length > 1 && (
                        <IconButton
                            onClick={() => setImageGallery((prev) => ({
                                ...prev,
                                currentIndex: prev.currentIndex === 0 ? prev.images.length - 1 : prev.currentIndex - 1
                            }))}
                            sx={{
                                position: 'absolute',
                                left: 16,
                                bgcolor: 'rgba(255,255,255,0.1)',
                                color: '#fff',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                                zIndex: 1,
                            }}
                        >
                            <ArrowBackIos sx={{ ml: 1 }} />
                        </IconButton>
                    )}

                    {/* Current Image */}
                    {imageGallery.images.length > 0 && (
                        <Box
                            component="img"
                            src={imageGallery.images[imageGallery.currentIndex]?.url}
                            alt={imageGallery.images[imageGallery.currentIndex]?.name}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: 'calc(95vh - 120px)',
                                objectFit: 'contain',
                            }}
                        />
                    )}

                    {/* Next Arrow */}
                    {imageGallery.images.length > 1 && (
                        <IconButton
                            onClick={() => setImageGallery((prev) => ({
                                ...prev,
                                currentIndex: prev.currentIndex === prev.images.length - 1 ? 0 : prev.currentIndex + 1
                            }))}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                bgcolor: 'rgba(255,255,255,0.1)',
                                color: '#fff',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                                zIndex: 1,
                            }}
                        >
                            <ArrowForwardIos />
                        </IconButton>
                    )}
                </DialogContent>

                {/* Thumbnail Strip */}
                {imageGallery.images.length > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, p: 2, bgcolor: 'rgba(0,0,0,0.8)' }}>
                        {imageGallery.images.map((img, index) => (
                            <Box
                                key={img.id}
                                component="img"
                                src={img.url}
                                alt={img.name}
                                onClick={() => setImageGallery((prev) => ({ ...prev, currentIndex: index }))}
                                sx={{
                                    width: 60,
                                    height: 60,
                                    objectFit: 'cover',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    opacity: index === imageGallery.currentIndex ? 1 : 0.5,
                                    border: index === imageGallery.currentIndex ? '2px solid #fff' : '2px solid transparent',
                                    transition: 'all 0.2s',
                                    '&:hover': { opacity: 1 },
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Dialog>
            {/* Citizen Details Dialog */}
            <CitizenDetailsDialog
                open={citizenDialog.open}
                onClose={() => setCitizenDialog({ open: false, citizen: null })}
                citizen={citizenDialog.citizen}
            />
        </Box>
    );
};

export default Complaints;

